import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReceivingFilters } from './components/ReceivingFilters';
import { ManufacturerGroupReceiving } from './components/ManufacturerGroupReceiving';
import { ReceiveBatchForm } from './components/ReceiveBatchForm';
import { useOrderedFramesGroupedByManufacturer } from './hooks/useReceiving';
import { Button } from '@/shared/components/ui/Button';
import { ActiveFiltersSummary } from '@/shared/components/ui/ActiveFiltersSummary';
import { supabase } from '@/shared/services/supabase';
import type { FrameFilters, Location, Manufacturer } from '@/shared/types/domain.types';

export function ReceivingPage() {
  const [filters, setFilters] = useState<FrameFilters>(() => {
    // Load location filter from localStorage if available
    const storedLocationIds = localStorage.getItem('selectedLocationIds');
    const locationIds = storedLocationIds ? JSON.parse(storedLocationIds) : undefined;

    return {
      ...(locationIds && locationIds.length > 0 ? { location_ids: locationIds } : {}),
    };
  });
  const [selectedFrameIds, setSelectedFrameIds] = useState<string[]>([]);
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(() => {
    const stored = localStorage.getItem('receivingFiltersVisible');
    return stored === null ? false : stored === 'true';
  });

  const { data: manufacturerGroups, isLoading, error, refetch } = useOrderedFramesGroupedByManufacturer(filters);

  // Fetch locations
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Location[];
    },
  });

  // Fetch manufacturers
  const { data: manufacturers } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Manufacturer[];
    },
  });

  // Build filter summary
  const filterSummary = useMemo(() => {
    const selectedLocations: { id: string; name: string }[] = [];
    const otherFilters: string[] = [];

    // Location filters (most important)
    if (filters.location_ids && filters.location_ids.length > 0) {
      filters.location_ids.forEach((id) => {
        const location = locations?.find((l) => l.id === id);
        if (location) selectedLocations.push({ id: location.id, name: location.name });
      });
    } else if (locations && locations.length > 0) {
      // Show all accessible locations if no filter is set
      selectedLocations.push(...locations.map((l) => ({ id: l.id, name: l.name })));
    }

    // Manufacturer filter
    if (filters.manufacturer_ids && filters.manufacturer_ids.length > 0) {
      const manufacturerNames = filters.manufacturer_ids
        .map((id) => manufacturers?.find((m) => m.id === id)?.name)
        .filter(Boolean);
      if (manufacturerNames.length > 0) {
        otherFilters.push(
          `Manufacturers: ${manufacturerNames.join(', ')}`
        );
      }
    }

    // Order type filter
    if (filters.order_type) {
      otherFilters.push(`Type: ${filters.order_type}`);
    }

    // Date range filter
    if (filters.date_from || filters.date_to) {
      const dateRange = [];
      if (filters.date_from) dateRange.push(`from ${filters.date_from}`);
      if (filters.date_to) dateRange.push(`to ${filters.date_to}`);
      otherFilters.push(`Date range: ${dateRange.join(' ')}`);
    }

    return { selectedLocations, otherFilters };
  }, [filters, locations, manufacturers]);

  const handleLocationAdd = (locationId: string) => {
    const currentLocationIds = filters.location_ids || [];
    const newLocationIds = [...currentLocationIds, locationId];

    // Save to localStorage
    localStorage.setItem('selectedLocationIds', JSON.stringify(newLocationIds));

    setFilters({
      ...filters,
      location_ids: newLocationIds,
    });
  };

  const handleLocationRemove = (locationId: string) => {
    let currentLocationIds = filters.location_ids || [];

    // If no filter is set (showing all locations), start with all location IDs
    if (currentLocationIds.length === 0 && locations) {
      currentLocationIds = locations.map((l) => l.id);
    }

    const newLocationIds = currentLocationIds.filter((id) => id !== locationId);

    // If no locations are selected, remove the filter entirely to show all locations
    if (newLocationIds.length === 0) {
      // Clear from localStorage
      localStorage.removeItem('selectedLocationIds');

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { location_ids, ...restFilters } = filters;
      setFilters(restFilters);
    } else {
      // Save to localStorage
      localStorage.setItem('selectedLocationIds', JSON.stringify(newLocationIds));

      setFilters({
        ...filters,
        location_ids: newLocationIds,
      });
    }
  };

  const handleFilterChange = (newFilters: FrameFilters) => {
    setFilters(newFilters);
    // Clear selection when filters change
    setSelectedFrameIds([]);
  };

  const handleReceiveSuccess = () => {
    // Clear selection and refetch data
    setSelectedFrameIds([]);
    refetch();
  };

  const toggleFilters = () => {
    const newValue = !showFilters;
    setShowFilters(newValue);
    localStorage.setItem('receivingFiltersVisible', String(newValue));
  };

  // Get all frames from all groups
  const allFrames = manufacturerGroups?.flatMap((group) => group.frames) || [];

  // Get selected frames
  const selectedFrames = allFrames.filter((f) => selectedFrameIds.includes(f.id));

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-3xl font-bold mb-6">Receiving</h1>
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-gray-600">Loading ordered frames...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-3xl font-bold mb-6">Receiving</h1>
        <div className="bg-white rounded-lg shadow p-8">
          <div className="text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">
              {error instanceof Error ? error.message : 'Failed to load ordered frames'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalFrames = manufacturerGroups?.reduce((sum, group) => sum + group.frameCount, 0) || 0;
  const totalSelected = selectedFrameIds.length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Receiving</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Mark ordered frames as received when they arrive at your locations
          </p>
        </div>
        <button
          onClick={toggleFilters}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 self-start sm:self-auto"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Active Filters Summary (shown when filters are hidden) */}
      {!showFilters && (
        <ActiveFiltersSummary
          selectedLocations={filterSummary.selectedLocations}
          availableLocations={locations?.map((l) => ({ id: l.id, name: l.name })) || []}
          otherFilters={filterSummary.otherFilters}
          onLocationAdd={handleLocationAdd}
          onLocationRemove={handleLocationRemove}
          onShowAllFilters={toggleFilters}
        />
      )}

      {/* Filters */}
      {showFilters && <ReceivingFilters filters={filters} onFilterChange={handleFilterChange} />}

      {/* Stats Bar */}
      {totalFrames > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-sm text-blue-700">Total Ordered Frames:</span>
                <span className="ml-2 text-lg font-bold text-blue-900">{totalFrames}</span>
              </div>
              <div className="h-6 w-px bg-blue-300"></div>
              <div>
                <span className="text-sm text-blue-700">Manufacturers:</span>
                <span className="ml-2 text-lg font-bold text-blue-900">
                  {manufacturerGroups?.length || 0}
                </span>
              </div>
              {totalSelected > 0 && (
                <>
                  <div className="h-6 w-px bg-blue-300"></div>
                  <div>
                    <span className="text-sm text-blue-700">Selected:</span>
                    <span className="ml-2 text-lg font-bold text-blue-900">{totalSelected}</span>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {totalSelected > 0 && (
                <button
                  onClick={() => setSelectedFrameIds([])}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear Selection
                </button>
              )}
              <Button
                onClick={() => setIsReceiveModalOpen(true)}
                variant="primary"
                disabled={totalSelected === 0}
              >
                Mark Selected as Received ({totalSelected})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!manufacturerGroups || manufacturerGroups.length === 0) && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            No frames waiting to be received
          </h2>
          <p className="text-gray-600 mb-6">
            {filters.location_ids?.length ||
            filters.manufacturer_ids?.length ||
            filters.order_type ||
            filters.date_from ||
            filters.date_to
              ? 'Try adjusting your filters to see ordered frames.'
              : 'All ordered frames have been received. Check back later or place new orders.'}
          </p>
          {(filters.location_ids?.length ||
            filters.manufacturer_ids?.length ||
            filters.order_type ||
            filters.date_from ||
            filters.date_to) && (
            <button
              onClick={() => handleFilterChange({})}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Manufacturer Groups */}
      {manufacturerGroups && manufacturerGroups.length > 0 && (
        <div className="space-y-6">
          {manufacturerGroups.map((group) => (
            <ManufacturerGroupReceiving
              key={group.manufacturer.id}
              group={group}
              selectedFrameIds={selectedFrameIds}
              onSelectionChange={setSelectedFrameIds}
              onReceiveSuccess={handleReceiveSuccess}
            />
          ))}
        </div>
      )}

      {/* Receive Modal (for batch receiving from stats bar) */}
      <ReceiveBatchForm
        isOpen={isReceiveModalOpen}
        onClose={() => setIsReceiveModalOpen(false)}
        frames={selectedFrames}
        onSuccess={handleReceiveSuccess}
      />
    </div>
  );
}
